import React, { useState, useEffect } from "react";
import { API_URL } from '../config';
import axios from 'axios';
import LoadingButton from "../components/LoadingButton";

const TripManagementPage = () => {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [diagnostic, setDiagnostic] = useState(null);

  const statusLabels = {
    created: '🆕 Создан',
    in_transit_to_zakalka: '🚚 В пути к закалке',
    delivered_to_zakalka: '🔥 Доставлен на закалку',
    in_transit_from_zakalka: '↩️ В пути с закалки',
    delivered_from_zakalka: '📥 Доставлен с закалки',
    completed: '✅ Завершен'
  };

  const statusColors = {
    created: 'bg-gray-100 text-gray-800',
    in_transit_to_zakalka: 'bg-blue-100 text-blue-800',
    delivered_to_zakalka: 'bg-orange-100 text-orange-800',
    in_transit_from_zakalka: 'bg-purple-100 text-purple-800',
    delivered_from_zakalka: 'bg-green-100 text-green-800',
    completed: 'bg-emerald-100 text-emerald-800'
  };

  const loadTrips = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API_URL}/api/batches`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        setTrips(response.data.batches);
      } else {
        throw new Error(response.data.message || 'Ошибка загрузки рейсов');
      }
    } catch (err) {
      console.error("Ошибка загрузки рейсов:", err);
      setError(`Ошибка загрузки: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const updateTripStatus = async (tripId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.put(`${API_URL}/api/batches/${tripId}/status`, {
        status: newStatus
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        await loadTrips();
        if (selectedTrip && selectedTrip.id === tripId) {
          setSelectedTrip({ ...selectedTrip, status: newStatus });
        }
      } else {
        throw new Error(response.data.message || 'Ошибка обновления статуса');
      }
    } catch (err) {
      console.error("Ошибка обновления статуса:", err);
      setError(`Ошибка обновления: ${err.message}`);
    }
  };

  const deleteTrip = async (tripId) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот рейс?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.delete(`${API_URL}/api/batches/${tripId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        await loadTrips();
        if (selectedTrip && selectedTrip.id === tripId) {
          setSelectedTrip(null);
          setShowDetails(false);
        }
      } else {
        throw new Error(response.data.message || 'Ошибка удаления рейса');
      }
    } catch (err) {
      console.error("Ошибка удаления рейса:", err);
      setError(`Ошибка удаления: ${err.message}`);
    }
  };

  const showTripDetails = async (tripId) => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API_URL}/api/batches/${tripId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        setSelectedTrip(response.data.batch);
        setShowDetails(true);
      } else {
        throw new Error(response.data.message || 'Ошибка загрузки деталей рейса');
      }
    } catch (err) {
      console.error("Ошибка загрузки деталей:", err);
      setError(`Ошибка загрузки деталей: ${err.message}`);
    }
  };

  const runDiagnostic = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/batches-diagnostic`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setDiagnostic(response.data);
        console.log('Диагностика рейсов:', response.data);
      }
    } catch (err) {
      console.error("Ошибка диагностики:", err);
      setError(`Ошибка диагностики: ${err.message}`);
    }
  };

  const cleanOldBatches = async () => {
    if (!window.confirm('Вы уверены, что хотите удалить все старые рейсы с JSON данными? Это действие необратимо!')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/clean-old-batches`, {
        confirmCleanup: true
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        alert(`Успешно удалено ${response.data.deletedBatches} старых рейсов`);
        await loadTrips();
        await runDiagnostic();
      }
    } catch (err) {
      console.error("Ошибка очистки:", err);
      setError(`Ошибка очистки: ${err.message}`);
    }
  };

  useEffect(() => {
    loadTrips();
    runDiagnostic();
  }, []);

  return (
    <div className="container mx-auto p-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Управление рейсами</h1>
        <div className="flex space-x-2">
          <button
            onClick={runDiagnostic}
            className="bg-yellow-500 hover:bg-yellow-700 text-white font-bold py-2 px-4 rounded text-sm"
          >
            🔍 Диагностика
          </button>
          {diagnostic && diagnostic.oldBatchesWithJSON > 0 && (
            <button
              onClick={cleanOldBatches}
              className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded text-sm"
            >
              🗑️ Очистить старые ({diagnostic.oldBatchesWithJSON})
            </button>
          )}
          <button
            onClick={loadTrips}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
          >
            🔄 Обновить
          </button>
        </div>
      </div>

      {/* Отображение диагностики */}
      {diagnostic && (
        <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded mb-4">
          <div className="flex justify-between items-center">
            <span>
              <strong>Диагностика:</strong> 
              {diagnostic.hasOrdersDataColumn 
                ? `Старых рейсов с JSON: ${diagnostic.oldBatchesWithJSON}, ` 
                : 'Колонка orders_data отсутствует (новая структура), '
              }
              Нормализованных рейсов: {diagnostic.newNormalizedBatches}
            </span>
            <button 
              onClick={() => setDiagnostic(null)}
              className="ml-2 text-blue-500 hover:text-blue-700"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Отображение ошибок */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          <strong>Ошибка:</strong> {error}
          <button 
            onClick={() => setError(null)}
            className="ml-2 text-red-500 hover:text-red-700"
          >
            ✕
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Список рейсов */}
        <div className="bg-white rounded-lg shadow-md">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium">Список рейсов</h2>
          </div>
          
          <div className="p-6">
            {loading ? (
              <div className="text-center py-4">Загрузка...</div>
            ) : (
              <div className="space-y-3">
                {trips.map((trip) => (
                  <div
                    key={trip.id}
                    className={`border rounded-lg p-4 cursor-pointer transition-all ${
                      selectedTrip && selectedTrip.id === trip.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => showTripDetails(trip.id)}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="font-medium">
                          Рейс {trip.batch_number || `№${trip.id}`}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {trip.direction === 'to_zakalka' ? '🔥 На закалку' : '📥 С закалки'}
                        </p>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full ${statusColors[trip.status]}`}>
                        {statusLabels[trip.status]}
                      </span>
                    </div>
                    
                    <div className="text-sm text-gray-500">
                      <div>Создан: {new Date(trip.created_at).toLocaleString('ru-RU')}</div>
                      <div>Заказов: {trip.orders_data?.length || 0}</div>
                      <div>Общее количество: {trip.total_quantity || 0}</div>
                    </div>
                  </div>
                ))}
                
                {trips.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    Рейсы не найдены
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Детали рейса */}
        <div className="bg-white rounded-lg shadow-md">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium">
              {selectedTrip ? `Детали рейса ${selectedTrip.batch_number || `№${selectedTrip.id}`}` : 'Выберите рейс'}
            </h2>
          </div>
          
          <div className="p-6">
            {!selectedTrip ? (
              <div className="text-center py-8 text-gray-500">
                Выберите рейс из списка для просмотра деталей
              </div>
            ) : (
              <div className="space-y-4">
                {/* Основная информация */}
                <div className="border-b pb-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium">Номер рейса:</span>
                      <span className="ml-2 font-mono">{selectedTrip.batch_number || `#${selectedTrip.id}`}</span>
                    </div>
                    <div>
                      <span className="font-medium">Статус:</span>
                      <div className={`inline-block ml-2 px-2 py-1 text-xs rounded-full ${statusColors[selectedTrip.status]}`}>
                        {statusLabels[selectedTrip.status]}
                      </div>
                    </div>
                    <div>
                      <span className="font-medium">Направление:</span>
                      <span className="ml-2">
                        {selectedTrip.direction === 'to_zakalka' ? '🔥 На закалку' : '📥 С закалки'}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium">Создан:</span>
                      <span className="ml-2">{new Date(selectedTrip.created_at).toLocaleString('ru-RU')}</span>
                    </div>
                    <div>
                      <span className="font-medium">Создал:</span>
                      <span className="ml-2">{selectedTrip.created_by || 'Неизвестно'}</span>
                    </div>
                  </div>
                </div>

                {/* Управление статусом */}
                <div className="border-b pb-4">
                  <h4 className="font-medium mb-2">Управление статусом</h4>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(statusLabels).map(([status, label]) => (
                      <button
                        key={status}
                        onClick={() => updateTripStatus(selectedTrip.id, status)}
                        disabled={selectedTrip.status === status}
                        className={`px-3 py-1 text-xs rounded ${
                          selectedTrip.status === status
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-blue-500 text-white hover:bg-blue-600'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Сводка по стеклу */}
                {selectedTrip.glass_summary && (
                  <div className="border-b pb-4">
                    <h4 className="font-medium mb-2">Сводка по стеклу</h4>
                    <div className="grid grid-cols-1 gap-2">
                      {Object.entries(selectedTrip.glass_summary).map(([type, qty]) => (
                        <div key={type} className="text-sm bg-gray-50 rounded px-3 py-2 flex justify-between">
                          <span className="font-medium">{type}</span>
                          <span>{qty} шт.</span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-2 text-sm text-gray-600">
                      <strong>Общее количество: {selectedTrip.total_quantity || 0} шт.</strong>
                    </div>
                  </div>
                )}

                {/* Заказы в рейсе */}
                {selectedTrip.orders_data && selectedTrip.orders_data.length > 0 && (
                  <div className="border-b pb-4">
                    <h4 className="font-medium mb-2">Заказы в рейсе ({selectedTrip.orders_data.length})</h4>
                    <div className="space-y-2">
                      {selectedTrip.orders_data.map((order, index) => (
                        <div key={index} className="text-sm bg-gray-50 rounded px-3 py-2">
                          <div className="font-medium">#{order.orderNumber} - {order.orderTitle}</div>
                          {order.selectedDetails && (
                            <div className="mt-1 text-xs text-gray-600">
                              Деталей: {order.selectedDetails.length}
                            </div>
                          )}
                          {order.comments && (
                            <div className="mt-1 text-xs text-gray-600">
                              Комментарий: {order.comments}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Комментарии водителя */}
                {selectedTrip.driver_comments && (
                  <div className="border-b pb-4">
                    <h4 className="font-medium mb-2">Комментарии водителя</h4>
                    <div className="text-sm bg-yellow-50 rounded px-3 py-2">
                      {selectedTrip.driver_comments}
                    </div>
                  </div>
                )}

                {/* Действия */}
                <div className="flex justify-end space-x-2">
                  <LoadingButton
                    onClick={() => deleteTrip(selectedTrip.id)}
                    label="🗑️ Удалить рейс"
                    color="red"
                    className="text-sm"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TripManagementPage;